import Button from '@components/common/button/Button';
import { CircularProgress, WithStyles, withStyles } from '@material-ui/core';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import PersonAddIcon from '@material-ui/icons/PersonAddOutlined';
import { isContactEditorAvailable, openEmailContact } from '@helpers/contactEditor';
import * as React from 'react';
import { EmailIntegrationSettingsStyle as styles } from './EmailIntegrationSettings.style';

export interface IEmailIntegrationSettingsCopy {
  heading: string;
  explanation: string;
  activate: string;
  activating: string;
  activationError: string;
  forwarding: string;
  copyAddress: string;
  copied: string;
  copyError: string;
  addContact: string;
  openingContacts: string;
  contactName: string;
  contactError: string;
  loading: string;
  unavailable: string;
  noFamily: string;
}

interface IProps extends WithStyles<typeof styles> {
  accountId?: number;
  familyId?: number;
  address?: string | null;
  activationPending?: boolean;
  activationFailed?: boolean;
  onActivate: () => void;
  copy: IEmailIntegrationSettingsCopy;
}

interface IState {
  copyStatus: 'idle' | 'copying' | 'copied' | 'failed';
  contactAvailable: boolean;
  contactStatus: 'idle' | 'opening' | 'failed';
}

export class EmailIntegrationSettingsComponent extends React.Component<IProps, IState> {
  public state: IState = { copyStatus: 'idle',
    contactAvailable: false, contactStatus: 'idle' };
  private mounted = false;
  private sequence = 0;
  private contactInFlight = false;
  private copyFeedbackTimeout?: number;

  public componentDidMount() {
    this.mounted = true;
    this.checkContactEditor();
    document.addEventListener('deviceready', this.checkContactEditor);
  }

  public componentDidUpdate(previous: IProps) {
    if (previous.accountId !== this.props.accountId || previous.familyId !== this.props.familyId
      || previous.address !== this.props.address) {
      this.sequence += 1;
      window.clearTimeout(this.copyFeedbackTimeout);
      this.setState({ copyStatus: 'idle', contactStatus: this.contactInFlight ? 'opening' : 'idle' });
    }
  }

  public componentWillUnmount() {
    this.mounted = false;
    this.sequence += 1;
    window.clearTimeout(this.copyFeedbackTimeout);
    document.removeEventListener('deviceready', this.checkContactEditor);
  }

  public render() {
    const { classes, copy, activationPending, activationFailed } = this.props;
    const { copyStatus, contactAvailable, contactStatus } = this.state;
    const address = this.address;
    const loading = Boolean(this.props.familyId) && this.props.address === undefined;
    return (
      <section className="settingsCard" aria-labelledby="email-integration-heading" aria-busy={loading || Boolean(activationPending)}>
        <h2 id="email-integration-heading" className={classes.heading}>{copy.heading}</h2>
        <p className={classes.text}>{copy.explanation}</p>
        {loading && (
          <div className={classes.loading} role="status">
            <CircularProgress size={24} aria-hidden="true" />{copy.loading}
          </div>
        )}
        {!loading && !address && this.props.familyId && this.props.address === null ? (
          <div className={classes.actions}>
            <Button color="primary" variant="contained" size="large" disabled={activationPending} onClick={this.props.onActivate}>
              {activationPending ? copy.activating : copy.activate}
            </Button>
          </div>
        ) : !loading && !address && (
          <p className={classes.text} role="status">{this.props.familyId ? copy.unavailable : copy.noFamily}</p>
        )}
        {!address && activationFailed && <p className={classes.error} role="alert">{copy.activationError}</p>}
        {!loading && address && (
          <>
            <section className={classes.integration} aria-labelledby="email-forwarding-heading">
              <h3 id="email-forwarding-heading" className={classes.subheading}>{copy.forwarding}</h3>
              <div className={classes.addressBox}>
                <p className={classes.address}>{address}</p>
                <div className={classes.copyActions}>
                  <Button color="primary" variant="outlined" size="large"
                    disabled={copyStatus === 'copying'} onClick={this.copyAddress}>
                    <CopyIcon fontSize="small" />
                    <span aria-live="polite">{copyStatus === 'copied' ? copy.copied : copy.copyAddress}</span>
                  </Button>
                  {contactAvailable && (
                    <Button color="primary" variant="outlined" size="large"
                      disabled={contactStatus === 'opening'} onClick={this.addContact}>
                      <PersonAddIcon fontSize="small" />
                      <span>{contactStatus === 'opening' ? copy.openingContacts : copy.addContact}</span>
                    </Button>
                  )}
                </div>
              </div>
              {copyStatus === 'failed' && <p className={classes.error} role="alert">{copy.copyError}</p>}
              {contactStatus === 'failed' && <p className={classes.error} role="alert">{copy.contactError}</p>}
            </section>
            {/* Google integration stays disabled until it is ready. */}
          </>
        )}
      </section>
    );
  }

  private get address(): string | null {
    const { familyId, address } = this.props;
    return familyId && typeof address === 'string' && /^[A-Za-z0-9_-]{1,64}@data\.co-parenting\.app$/.test(address)
      ? address : null;
  }

  private checkContactEditor = async () => {
    const contactAvailable = await isContactEditorAvailable();
    if (this.mounted) { this.setState({ contactAvailable }); }
  };

  private addContact = async () => {
    const address = this.address;
    if (!address || !this.state.contactAvailable || this.contactInFlight) { return; }
    this.contactInFlight = true;
    const sequence = this.sequence;
    this.setState({ contactStatus: 'opening' });
    try {
      await openEmailContact(this.props.copy.contactName, address);
      if (this.mounted && sequence === this.sequence) { this.setState({ contactStatus: 'idle' }); }
    } catch (error) {
      if (this.mounted && sequence === this.sequence) { this.setState({ contactStatus: 'failed' }); }
    } finally {
      this.contactInFlight = false;
      // A snapshot may have supplied a new address while the editor was open.
      if (this.mounted && sequence !== this.sequence) { this.setState({ contactStatus: 'idle' }); }
    }
  };

  private copyAddress = async () => {
    const address = this.address;
    if (!address || this.state.copyStatus === 'copying') { return; }
    const sequence = this.sequence;
    window.clearTimeout(this.copyFeedbackTimeout);
    this.setState({ copyStatus: 'copying' });
    try {
      await copyTextToClipboard(address);
      if (!this.mounted || sequence !== this.sequence) { return; }
      this.setState({ copyStatus: 'copied' });
      this.copyFeedbackTimeout = window.setTimeout(() => this.setState({ copyStatus: 'idle' }), 2500);
    } catch (error) {
      if (this.mounted && sequence === this.sequence) { this.setState({ copyStatus: 'failed' }); }
    }
  };
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // Some app WebViews deny Clipboard API access but support copying a selection.
    }
  }
  const previousFocus = document.activeElement as HTMLElement | null;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.readOnly = true;
  textarea.tabIndex = -1;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.fontSize = '16px';
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    if (!document.execCommand || !document.execCommand('copy')) {
      throw new Error('Clipboard copy failed');
    }
  } finally {
    document.body.removeChild(textarea);
    if (previousFocus && previousFocus.focus) { previousFocus.focus(); }
  }
}

export default withStyles(styles)(EmailIntegrationSettingsComponent);



// WEBPACK FOOTER //
// ./src/components/integrations/email/EmailIntegrationSettings.tsx